import type {AgentType, LLMRole} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExcludeInaccessible from 'graphql-client/ExcludeInaccessible';
import type {ProgressData} from 'llm/chat-library/progress/ProgressTypes';
import type {DeepResearchToolType} from 'llm/deep-research/tool-details/types';

import type {ChatSessionQuery} from '../__generated/ChatSessionQuery';

export enum ProgressStatus {
  STARTED = 'STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
}

export interface ClientStreamingAssistantMessage {
  __typename: 'ClientStreamingAssistantMessage';
  responseToEventId: string;
  messageBlocks: Array<DisplayTextBlockNode>;
  role: LLMRole.ASSISTANT;
  agentType: AgentType;
  createdAt: string;
}

export interface ClientOptimisticUserMessage {
  __typename: 'ClientOptimisticUserMessage';
  role: LLMRole.USER;
  agentType: AgentType;
  messageBlocks: Array<DisplayTextBlockNode | Omit<DisplayFileBlockNode, 'externalFileId'>>;
}

/**
 * A progress message that can be displayed inline in the chat thread.
 * Used for both in-progress and completed thinking sections.
 */
export interface ClientProgressMessage {
  __typename: 'ClientProgressMessage';
  status: ProgressStatus;
  errorMessage: string | null;
  agentType: AgentType | null;
  role: null;
  progressData: ProgressData | null;
  /** ID of the assistant message this progress section is associated with (for completed sections) */
  associatedMessageId?: string;
}

export type ChatEventNode = ExcludeInaccessible<
  ChatSessionQuery['chatSession']
>['threads'][number]['events'][number];

export type DisplayMessageChatEventNode = ChatEventNode & {__typename: 'DisplayMessageChatEventNode'};
export type StateUpdateChatEventNode = ChatEventNode & {__typename: 'StateUpdateChatEventNode'};

export type DisplayTextBlockNode = DisplayMessageChatEventNode['messageBlocks'][number] & {
  __typename: 'DisplayTextBlockNode';
};
export type DisplayFileBlockNode = DisplayMessageChatEventNode['messageBlocks'][number] & {
  __typename: 'DisplayFileBlockNode';
};

// LLM Message Block Types - mirroring Python backend structure

export interface LLMTextBlock {
  type: 'text';
  value: {
    text: string;
  };
}

export type LLMImageMediaType = 'image/jpeg' | 'image/png' | 'image/webp';

export interface LLMImageBlock {
  type: 'image';
  value: {
    base64_image: string;
    media_type: LLMImageMediaType;
  };
}

export interface LLMToolCallBlock {
  type: 'tool_call';
  value: {
    id: string;
    // Allow both enum values and strings for MCP tools (e.g., "server.tool_name")
    name: DeepResearchToolType | string;
    args: Record<string, unknown>;
  };
}

export interface LLMToolResultBlock {
  type: 'tool_result';
  value: {
    id: string;
    content: Array<LLMTextBlock | LLMImageBlock>;
  };
}

// All Client-only events are "display" events.
export type ClientOnlyChatEvent =
  | ClientOptimisticUserMessage
  | ClientProgressMessage
  | ClientStreamingAssistantMessage;

// In case server types and client types are mixed together (Deep Research)
export type ClientChatEvent = ClientOnlyChatEvent | ChatEventNode;

// Filtering down to only display events. Keep in sync with MessageUtils.ts:filterDisplayEvents implementation.
export type ClientDisplayChatEvent = ClientOnlyChatEvent | DisplayMessageChatEventNode;

/**
 * For a DisplayMessageChatEventNode with role: LLMRole.ASSISTANT,
 * indicate how the message should be presented.
 *
 * Ideally this enum value comes from the persisted message, but as of 2025-09-17, it is derived from
 * DisplayMessageChatEventNode.agentType (see: getDisplayMessageAssistantResponseType).
 */
export enum DisplayMessageAssistantResponseType {
  REPLY = 'REPLY',
  REPORT = 'REPORT',
}
